import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { 
  BarChart3, TrendingUp, MousePointer, Smartphone, Monitor,
  RefreshCw, ArrowLeft, Zap, Bitcoin, DollarSign, Calendar, Trash2
} from 'lucide-react';
import { motion } from 'framer-motion';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AffiliateAnalyticsPage = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [clearing, setClearing] = useState(false);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/affiliate/analytics`, {
        credentials: 'include'
      });
      if (!response.ok) throw new Error('Failed to fetch analytics');
      const data = await response.json();
      setAnalytics(data);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleClearTestData = async () => {
    const confirmed = window.confirm(
      '⚠️ ВНИМАНИЕ!\n\n' +
      'Това ще изтрие ВСИЧКИ affiliate данни:\n' +
      '• Всички clicks\n' +
      '• Всички tool uses\n\n' +
      'Тази операция е необратима!\n\n' +
      'Сигурен ли си, че искаш да продължиш?'
    );
    
    if (!confirmed) return;
    
    setClearing(true);
    try {
      const response = await fetch(`${API_URL}/api/affiliate/clear-test-data`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to clear data');
      }
      
      const result = await response.json();
      alert(
        `✅ Данните са изчистени успешно!\n\n` +
        `Изтрити clicks: ${result.deleted.affiliate_clicks}\n` +
        `Изтрити tool uses: ${result.deleted.affiliate_tool_uses}`
      );
      
      // Refresh analytics
      fetchAnalytics();
    } catch (err) {
      alert(`❌ Грешка: ${err.message}`);
    } finally {
      setClearing(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4">
        <p className="text-red-400">{error}</p>
        <Button onClick={fetchAnalytics}>Retry</Button>
      </div>
    );
  }

  const { summary, clicks_by_platform, clicks_by_tool, ctr_by_tool, clicks_by_crypto, daily_stats, recent_clicks } = analytics || {};

  // Sort tools by clicks
  const sortedTools = Object.entries(clicks_by_tool || {})
    .sort(([, a], [, b]) => b - a);

  // Sort cryptos by clicks
  const sortedCryptos = Object.entries(clicks_by_crypto || {})
    .sort(([, a], [, b]) => b - a);

  // Sort daily stats
  const sortedDays = Object.entries(daily_stats || {})
    .sort(([a], [b]) => b.localeCompare(a))
    .slice(0, 7);

  return (
    <>
      <SEOHead
        title="Affiliate Analytics Dashboard | ToolsForge Admin"
        description="Track affiliate click performance across crypto calculators"
        noIndex={true}
      />

      <div className="min-h-screen py-20 px-6" data-testid="affiliate-analytics-page">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
            <div>
              <Link to="/" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-4">
                <ArrowLeft className="w-4 h-4" />
                Back to Home
              </Link>
              <h1 className="text-3xl font-bold font-['Plus_Jakarta_Sans']">
                Affiliate Analytics
              </h1>
              <p className="text-muted-foreground mt-1">
                Track click performance across crypto calculator tools
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button onClick={fetchAnalytics} variant="outline" size="sm">
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
              <Button 
                onClick={handleClearTestData} 
                variant="destructive" 
                size="sm"
                disabled={clearing}
                data-testid="clear-test-data-btn"
              >
                {clearing ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4 mr-2" />
                )}
                {clearing ? 'Clearing...' : 'Clear Test Data'}
              </Button>
            </div>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="p-4 rounded-xl border border-border bg-card"
            >
              <div className="flex items-center gap-2 text-muted-foreground mb-2">
                <MousePointer className="w-4 h-4" />
                <span className="text-xs">Total Clicks</span>
              </div>
              <p className="text-2xl font-bold">{summary?.total_clicks || 0}</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05 }}
              className="p-4 rounded-xl border border-border bg-card"
            >
              <div className="flex items-center gap-2 text-muted-foreground mb-2">
                <BarChart3 className="w-4 h-4" />
                <span className="text-xs">Tool Uses</span>
              </div>
              <p className="text-2xl font-bold">{summary?.total_tool_uses || 0}</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="p-4 rounded-xl border border-border bg-card"
            >
              <div className="flex items-center gap-2 text-muted-foreground mb-2">
                <TrendingUp className="w-4 h-4" />
                <span className="text-xs">Overall CTR</span>
              </div>
              <p className="text-2xl font-bold text-green-400">{summary?.overall_ctr || 0}%</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
              className="p-4 rounded-xl border border-yellow-500/30 bg-yellow-500/10"
            >
              <div className="flex items-center gap-2 text-yellow-400 mb-2">
                <span className="text-lg font-bold">B</span>
                <span className="text-xs">Binance</span>
              </div>
              <p className="text-2xl font-bold text-yellow-400">{summary?.binance_clicks || 0}</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="p-4 rounded-xl border border-cyan-500/30 bg-cyan-500/10"
            >
              <div className="flex items-center gap-2 text-cyan-400 mb-2">
                <Zap className="w-4 h-4" />
                <span className="text-xs">MEXC</span>
              </div>
              <p className="text-2xl font-bold text-cyan-400">{summary?.mexc_clicks || 0}</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.25 }}
              className="p-4 rounded-xl border border-border bg-card"
            >
              <div className="flex items-center gap-2 text-muted-foreground mb-2">
                <Smartphone className="w-4 h-4" />
                <span className="text-xs">Mobile</span>
              </div>
              <p className="text-2xl font-bold">{summary?.mobile_clicks || 0}</p>
              <p className="text-xs text-muted-foreground">
                Desktop: {summary?.desktop_clicks || 0}
              </p>
            </motion.div>
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            {/* Clicks by Tool */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="p-6 rounded-xl border border-border bg-card"
            >
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-primary" />
                Clicks by Tool
              </h2>
              {sortedTools.length > 0 ? (
                <div className="space-y-3">
                  {sortedTools.map(([tool, clicks], index) => {
                    const ctr = ctr_by_tool?.[tool]?.ctr || 0;
                    const maxClicks = sortedTools[0]?.[1] || 1;
                    const percentage = (clicks / maxClicks) * 100;
                    
                    return (
                      <div key={tool} className="space-y-1">
                        <div className="flex items-center justify-between text-sm">
                          <span className="truncate max-w-[200px]">{tool}</span>
                          <span className="flex items-center gap-2">
                            <Badge variant="outline" className="text-xs">{clicks} clicks</Badge>
                            <Badge variant="secondary" className="text-xs text-green-400">{ctr}% CTR</Badge>
                          </span>
                        </div>
                        <div className="h-2 bg-muted rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-primary rounded-full transition-all duration-500"
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-muted-foreground text-sm">No click data yet</p>
              )}
            </motion.div>

            {/* Clicks by Crypto */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35 }}
              className="p-6 rounded-xl border border-border bg-card"
            >
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Bitcoin className="w-5 h-5 text-orange-400" />
                Top Performing Crypto
              </h2>
              {sortedCryptos.length > 0 ? (
                <div className="space-y-3">
                  {sortedCryptos.map(([crypto, clicks]) => {
                    const maxClicks = sortedCryptos[0]?.[1] || 1;
                    const percentage = (clicks / maxClicks) * 100;
                    
                    return (
                      <div key={crypto} className="space-y-1">
                        <div className="flex items-center justify-between text-sm">
                          <span className="font-medium">{crypto}</span>
                          <Badge variant="outline">{clicks} clicks</Badge>
                        </div>
                        <div className="h-2 bg-muted rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-orange-400 rounded-full transition-all duration-500"
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-muted-foreground text-sm">No crypto data yet</p>
              )}
            </motion.div>

            {/* Daily Stats */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="p-6 rounded-xl border border-border bg-card"
            >
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-purple-400" />
                Daily Performance (Last 7 Days)
              </h2>
              {sortedDays.length > 0 ? (
                <div className="space-y-2">
                  {sortedDays.map(([date, stats]) => (
                    <div key={date} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                      <span className="text-sm text-muted-foreground">{date}</span>
                      <div className="flex items-center gap-3">
                        <Badge variant="outline" className="text-yellow-400 border-yellow-500/30">
                          B: {stats.binance}
                        </Badge>
                        <Badge variant="outline" className="text-cyan-400 border-cyan-500/30">
                          M: {stats.mexc}
                        </Badge>
                        <Badge variant="secondary">
                          Total: {stats.total}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground text-sm">No daily data yet</p>
              )}
            </motion.div>

            {/* Recent Clicks */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.45 }}
              className="p-6 rounded-xl border border-border bg-card"
            >
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <MousePointer className="w-5 h-5 text-green-400" />
                Recent Clicks
              </h2>
              {recent_clicks?.length > 0 ? (
                <div className="space-y-2 max-h-[300px] overflow-y-auto">
                  {recent_clicks.map((click) => (
                    <div key={click.click_id} className="flex items-center justify-between py-2 border-b border-border last:border-0 text-sm">
                      <div className="flex items-center gap-2">
                        {click.platform === 'binance' ? (
                          <span className="w-6 h-6 rounded-full bg-yellow-500/20 flex items-center justify-center text-xs font-bold text-yellow-400">B</span>
                        ) : (
                          <Zap className="w-5 h-5 text-cyan-400" />
                        )}
                        <div>
                          <p className="text-xs font-medium truncate max-w-[150px]">{click.tool_name}</p>
                          <p className="text-xs text-muted-foreground">{click.crypto_type}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <Badge variant={click.device_type === 'mobile' ? 'secondary' : 'outline'} className="text-xs">
                          {click.device_type === 'mobile' ? <Smartphone className="w-3 h-3" /> : <Monitor className="w-3 h-3" />}
                        </Badge>
                        <p className="text-xs text-muted-foreground mt-1">
                          {new Date(click.timestamp).toLocaleTimeString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground text-sm">No recent clicks</p>
              )}
            </motion.div>
          </div>

          {/* Platform Comparison */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="mt-6 p-6 rounded-xl border border-border bg-card"
          >
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-green-400" />
              Platform Comparison
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="p-4 rounded-lg border border-yellow-500/30 bg-yellow-500/5">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className="w-10 h-10 rounded-full bg-yellow-500/20 flex items-center justify-center text-xl font-bold text-yellow-400">B</span>
                    <span className="font-semibold">Binance</span>
                  </div>
                  <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">
                    {summary?.binance_clicks || 0} clicks
                  </Badge>
                </div>
                <div className="h-3 bg-muted rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-yellow-400 rounded-full"
                    style={{ 
                      width: `${summary?.total_clicks ? ((summary.binance_clicks / summary.total_clicks) * 100) : 0}%` 
                    }}
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  {summary?.total_clicks ? Math.round((summary.binance_clicks / summary.total_clicks) * 100) : 0}% of total clicks
                </p>
              </div>

              <div className="p-4 rounded-lg border border-cyan-500/30 bg-cyan-500/5">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-10 rounded-full bg-cyan-500/20 flex items-center justify-center">
                      <Zap className="w-5 h-5 text-cyan-400" />
                    </div>
                    <span className="font-semibold">MEXC</span>
                  </div>
                  <Badge className="bg-cyan-500/20 text-cyan-400 border-cyan-500/30">
                    {summary?.mexc_clicks || 0} clicks
                  </Badge>
                </div>
                <div className="h-3 bg-muted rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-cyan-400 rounded-full"
                    style={{ 
                      width: `${summary?.total_clicks ? ((summary.mexc_clicks / summary.total_clicks) * 100) : 0}%` 
                    }}
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  {summary?.total_clicks ? Math.round((summary.mexc_clicks / summary.total_clicks) * 100) : 0}% of total clicks
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </>
  );
};

export default AffiliateAnalyticsPage;
